import { Button } from '../../../components/ui/atoms/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from '../../../components/ui/organisms/dialog';
import { cn } from '../../../lib/utils';
import { ConfirmSendDialogProps, RecipientListProps } from './interface';
import { canPick, formatDateTime } from './utils';

export function RecipientList({ recipients, selected, resend, onToggle }: RecipientListProps) {
  if (recipients.length === 0) {
    return <p className="p-4 text-sm text-muted-foreground">No users match.</p>;
  }
  return (
    <ul className="max-h-96 divide-y overflow-y-auto rounded-md border">
      {recipients.map((recipient) => {
        const pickable = canPick(recipient, resend);
        return (
          <li key={recipient.id}>
            <label
              className={cn(
                'flex items-center gap-3 px-3 py-2',
                pickable ? 'cursor-pointer hover:bg-muted/50' : 'cursor-not-allowed opacity-60',
              )}
            >
              <input
                type="checkbox"
                className="h-4 w-4 accent-primary"
                checked={selected.includes(recipient.id)}
                disabled={!pickable}
                onChange={() => onToggle(recipient.id)}
                aria-label={recipient.name}
              />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium">{recipient.name}</span>
                <span className="block truncate text-xs text-muted-foreground">{recipient.email}</span>
              </span>
              {recipient.isGroup && <span className="shrink-0 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">Group</span>}
              {recipient.sentAt && (
                <span className="shrink-0 whitespace-nowrap rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400">
                  Sent {formatDateTime(recipient.sentAt)}
                </span>
              )}
            </label>
          </li>
        );
      })}
    </ul>
  );
}

export function ConfirmSendDialog({
  open, onOpenChange, version, count, alreadySentCount, pending, error, onConfirm,
}: ConfirmSendDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{`Send v${version} to ${count} ${count === 1 ? 'person' : 'people'}?`}</DialogTitle>
        </DialogHeader>
        <div className="space-y-2 text-sm">
          <p>Real emails go out as soon as you confirm. A sent email cannot be recalled.</p>
          {alreadySentCount > 0 && (
            <p className="text-amber-700 dark:text-amber-400">
              {alreadySentCount} of them already got this version and will get it again.
            </p>
          )}
          {error && <p className="text-destructive">{error}</p>}
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <DialogClose asChild>
            <Button variant="outline" disabled={pending}>Cancel</Button>
          </DialogClose>
          <Button onClick={onConfirm} disabled={pending}>
            {pending ? 'Sending…' : 'Send'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
